import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";
import { Loader2, ArrowRight } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage({ onAuth }) {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = mode === "register" ? await api.register(form) : await api.login({ email: form.email, password: form.password });
      setToken(res.token);
      onAuth(res.user);
      toast.success(mode === "register" ? "Headquarters online" : "Welcome back");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 hq-grid overflow-hidden">
      {/* left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 30% 30%, rgba(0,240,255,0.15), transparent 55%), radial-gradient(circle at 70% 70%, rgba(167,139,250,0.12), transparent 55%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl grid place-items-center bg-cyan-500/10 border border-cyan-400/40">
            <span className="absolute w-2 h-2 rounded-full bg-cyan-400 anim-flicker" style={{ boxShadow: "0 0 10px #00f0ff" }} />
            <span className="font-display font-black text-cyan-300 text-sm">OS</span>
          </div>
          <div className="font-display font-black tracking-widest text-2xl">LIFE<span className="text-cyan-400">//</span>OS</div>
        </div>
        <div className="relative">
          <h1 className="font-display font-black uppercase tracking-tight text-4xl xl:text-5xl leading-[1.05] text-zinc-50">
            Build your<br /><span className="text-cyan-400">AI workforce</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-base max-w-md leading-relaxed">
            Hire autonomous AI agents into a living virtual headquarters. Watch them work, collaborate, and run your business and personal life — 24/7.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Autonomous agents", "Real-time collaboration", "Connect Gmail & Calendar", "Always-on schedules"].map((t) => (
              <span key={t} className="text-[11px] font-mono px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-zinc-400">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative text-[11px] font-mono text-zinc-600">© LifeOS · Visual AI Operating System</div>
      </div>

      {/* right auth form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="font-display font-black tracking-widest text-2xl">LIFE<span className="text-cyan-400">//</span>OS</div>
          </div>
          <h2 className="font-display font-bold uppercase tracking-wide text-2xl text-zinc-50">{mode === "register" ? "Create account" : "Sign in"}</h2>
          <p className="text-zinc-500 text-sm mt-1 font-mono">{mode === "register" ? "Start with 50 free actions / month." : "Access your headquarters."}</p>

          <button data-testid="google-signin-button" onClick={googleLogin} className="mt-6 w-full h-11 rounded-xl bg-white text-zinc-800 font-semibold flex items-center justify-center gap-2 hover:bg-zinc-100 transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono">or email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Name</Label>
                <Input data-testid="auth-name" className="bg-[#0b0e14] border-white/10 text-zinc-100" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Email</Label>
              <Input data-testid="auth-email" type="email" required className="bg-[#0b0e14] border-white/10 text-zinc-100" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Password</Label>
              <Input data-testid="auth-password" type="password" required minLength={6} className="bg-[#0b0e14] border-white/10 text-zinc-100" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" />
            </div>
            <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.35)]">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>{mode === "register" ? "Create account" : "Sign in"} <ArrowRight size={16} className="ml-1" /></>}
            </Button>
          </form>

          <div className="mt-5 text-center text-sm text-zinc-500">
            {mode === "register" ? "Already have an account?" : "New to LifeOS?"}{" "}
            <button data-testid="auth-toggle" onClick={() => setMode(mode === "register" ? "login" : "register")} className="text-cyan-400 hover:text-cyan-300 font-semibold">
              {mode === "register" ? "Sign in" : "Create one"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
